/**
 * Can Slot Tracker (Spotlight theme build)
 *
 * Counts cart line items matching a given `product_type` and renders
 * a visual 6-pack that swaps to a 12-slot case once quantity exceeds 6.
 *
 * Hooks directly into Spotlight/Dawn's built-in cart pub/sub system
 * (constants.js + pubsub.js, loaded globally before this file), so it
 * updates automatically after add-to-cart, quantity changes, and
 * removals — no extra wiring needed anywhere else in the theme.
 *
 * To filter by tag or collection instead of product_type, edit the
 * `countMatchingCans` function below. The /cart.js item objects also
 * include vendor and product_id if you need to cross-reference a
 * collection instead.
 */

(function () {
  function init() {
    const trackers = document.querySelectorAll('[data-can-tracker]');
    console.log(`[Can Tracker] init: found ${trackers.length} tracker element(s) on this page.`);
    if (!trackers.length) return;

    const CAN_SVG_EMPTY = `
      <svg viewBox="0 0 40 40" class="can-tracker__can" aria-hidden="true">
        <circle cx="20" cy="20" r="17" class="can-tracker__can-lid" />
        <ellipse cx="20" cy="19" rx="12.5" ry="10" class="can-tracker__can-score" />
        <line x1="20" y1="14.5" x2="20" y2="11.5" class="can-tracker__can-stem" />
        <ellipse cx="20" cy="9" rx="5.5" ry="3.2" class="can-tracker__can-tab" />
        <circle cx="20" cy="14.5" r="1.4" class="can-tracker__can-rivet" />
      </svg>
    `;

    function countMatchingCans(cart, productType) {
      const target = productType.trim().toLowerCase();
      return cart.items
        .filter((item) => (item.product_type || '').trim().toLowerCase() === target)
        .reduce((sum, item) => sum + item.quantity, 0);
    }

    function getCaseState(qty) {
      if (qty <= 6) {
        const remaining = 6 - qty;
        let message;
        if (qty === 0) {
          message = 'Add a can to get started!';
        } else if (remaining > 0) {
          message = `${remaining} more can${remaining === 1 ? '' : 's'} to complete your 6-pack!`;
        } else {
          message = '6-pack complete! Add one more to start a 12-pack case.';
        }
        return { size: 6, filled: qty, title: 'Build Your 6-Pack', message };
      }

      const filledInCase = qty % 12 === 0 ? 12 : qty % 12;
      const caseNumber = Math.floor((qty - filledInCase) / 12) + 1;
      const remaining = 12 - filledInCase;
      const message =
        remaining > 0
          ? `${remaining} more can${remaining === 1 ? '' : 's'} to complete this case!`
          : 'Case complete! 🎉 Add more to start another.';

      return {
        size: 12,
        filled: filledInCase,
        remaining,
        title: caseNumber > 1 ? `Building Case ${caseNumber}` : 'Build Your Case (12-Pack)',
        message,
      };
    }

    function renderTracker(tracker, state) {
      const grid = tracker.querySelector('[data-can-tracker-grid]');
      const title = tracker.querySelector('[data-can-tracker-title]');
      const message = tracker.querySelector('[data-can-tracker-message]');

      title.textContent = state.title;
      message.textContent = state.message;

      const currentSize = parseInt(grid.dataset.size, 10);
      if (currentSize !== state.size || grid.children.length !== state.size) {
        grid.dataset.size = state.size;
        grid.className = `can-tracker__grid can-tracker__grid--${state.size}`;
        grid.innerHTML = '';
        for (let i = 0; i < state.size; i++) {
          const slot = document.createElement('div');
          slot.className = 'can-tracker__slot';
          slot.innerHTML = CAN_SVG_EMPTY;
          grid.appendChild(slot);
        }
      }

      const slots = grid.querySelectorAll('.can-tracker__slot');
      slots.forEach((slot, index) => {
        slot.classList.toggle('is-filled', index < state.filled);
      });

      if (state.filled === state.size) {
        tracker.classList.add('can-tracker--complete');
        grid.classList.add('can-tracker__grid--just-completed');
        setTimeout(() => grid.classList.remove('can-tracker__grid--just-completed'), 600);
      } else {
        tracker.classList.remove('can-tracker--complete');
      }

      // Case-mode nudge: only relevant once the shopper has moved into
      // the 12-slot case view. "Near complete" adds emphasis in the
      // last couple of cans so it doesn't shout the whole time.
      tracker.classList.toggle('can-tracker--case-mode', state.size === 12);
      tracker.classList.toggle(
        'can-tracker--near-complete',
        state.size === 12 && state.remaining > 0 && state.remaining <= 2
      );
    }

    async function refreshAll() {
      let cart;
      try {
        const res = await fetch('/cart.js');
        cart = await res.json();
      } catch (err) {
        console.error('[Can Tracker] Could not fetch /cart.js', err);
        return;
      }

      const distinctTypes = [...new Set(cart.items.map((i) => `"${i.product_type}"`))];
      console.log(
        `[Can Tracker] refreshAll: cart has ${cart.item_count} item(s) across ${cart.items.length} line(s). Distinct product_type values in cart: [${distinctTypes.join(', ') || 'none — cart is empty'}]`
      );

      trackers.forEach((tracker, i) => {
        const productType = tracker.dataset.productType || 'Maverick Beer';
        const qty = countMatchingCans(cart, productType);
        console.log(
          `[Can Tracker] tracker #${i + 1}: filtering for product_type "${productType}" → matched ${qty} can(s).`
        );
        const state = getCaseState(qty);
        renderTracker(tracker, state);
      });
    }

    // Initial paint
    refreshAll();

    // Spotlight/Dawn's theme-wide cart event bus (see constants.js + pubsub.js).
    // We ignore the event payload and re-fetch /cart.js directly since the
    // payload shape differs depending on which part of the theme triggered
    // it (product form vs. cart drawer vs. quick add) — fetching fresh is
    // simpler and always accurate.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      console.log('[Can Tracker] Successfully subscribed to theme cartUpdate events.');
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        console.log('[Can Tracker] cartUpdate event received — refreshing.');
        refreshAll();
      });
    } else {
      console.warn(
        '[Can Tracker] Could not find subscribe()/PUB_SUB_EVENTS — this tracker will only update on page load, not live. Check that constants.js and pubsub.js are loading before this script.'
      );
    }

    // Manual fallback hook, e.g. for custom code elsewhere in the theme.
    window.CanSlotTracker = { refresh: refreshAll };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
